'use no memo';
import React, { useContext } from 'react';
import type { Dispatch } from 'react';
import { Subscribe } from 'unstated';

import ScreenIcon from '@atlaskit/icon/core/screen';
import IconButton from '@atlaskit/button/icon/button';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { EditContentButton } from '@confluence/edit-button/entry-points/EditContentButton.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { ContentOwnershipContextProvider } from '@confluence/content-ownership/entry-points/ContentOwnershipContext.ts';
import type { ContentType as ShareContentType } from '@confluence/share-and-restrict-dialog/entry-points/ShareRestrictAndCopyCoordinator.tsx';
import { type ContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import {
	type ContentType,
	VALID_OPERATION_TARGETS,
	isCanvasContentType,
} from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { useIsClassificationEnabledForContentTypeExpansion } from '@confluence/data-classification/entry-points/hooks.ts';
import { isExternalShareRequest } from '@confluence/external-share-utils/entry-points/isExternalShareRequest.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { PublishButtonWithMoreOptions } from '@confluence/publish-modal/entry-points/PublishButtonWithMoreOptions.ts';
import { PreviewPanelTestButton } from '@confluence/preview-panel-api/entry-points/PreviewPanelTestButton.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import type { WhiteboardAIAgentServiceInterface } from '@confluence/whiteboard-types/entry-points/WhiteboardAIAgentService.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { AgentPresenceLoader } from '@confluence/team-presence/entry-points/AgentPresenceLoader.tsx';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { LoomExternalOpenButton } from '@confluence/loom-external-open/entry-points/LoomExternalOpenButton.ts';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useEditorObjectHeaderContext } from './EditorObjectHeaderContext.tsx';
import { ContentRestrictionsTrigger } from './content-types-actions/ContentRestrictionsTrigger.tsx';
import { ArchivedCopyLinkTrigger } from './content-types-actions/ArchivedCopyLinkTrigger.tsx';
import { MainActionsComponent } from './content-types-actions/MainActions.tsx';
import { MoreActionsComponent } from './content-types-actions/MoreActions.tsx';
import { PresentSlidesButton } from './content-types-actions/PresentSlidesButton.tsx';
import type {
	ContentActions,
	HeaderItems,
	ContentTypesHeaderQueryNode,
} from './ContentTypesHeader.tsx';
import type { HeaderFeaturesType } from './ContentTypesHeaderFeatures.tsx';
import { Presence } from './object-header-items/Presence.tsx';
import { EditorCloseButton } from './object-header-items/EditorCloseButton.tsx';
import { UnpublishedChangesOrLastEdited } from './object-header-items/UnpublishedChangesOrLastEdited.tsx';
import { useDisableEditorActions } from './useDisableEditorActions.tsx';
import { useObjectHeaderVersion } from './useObjectHeaderVersion.ts';
import { useIsPrivacyModeEnabled } from './useIsPrivacyModeEnabled.tsx';

export type HeaderItemData = {
	contentId: string;
	contentType: ShareContentType;
	contentMode: ContentMode | undefined;
	contentActions?: ContentActions;
	handleRegisterOnShowHeaderSpotlight?: (callback: () => void) => void;
	allowedFeatures: HeaderFeaturesType;
	contentData: ContentTypesHeaderQueryNode | null | undefined;
	renderCopyLink: () => React.JSX.Element | undefined;
	renderShare: (opts?: { isNotifyDisabled?: boolean }) => React.JSX.Element;
	isSpaceOverviewPage: boolean;
	readOnly?: boolean;
	isConvertToFolderHighlighted?: boolean;
	setIsConvertToFolderHighlighted?: Dispatch<React.SetStateAction<boolean>>;
	whiteboardAIAgentService?: WhiteboardAIAgentServiceInterface;
	isVersionHistoryPage?: boolean;
	isAttachmentsPage?: boolean;
};

export const useHeaderItems = ({
	contentId,
	contentType,
	contentMode,
	contentActions,
	handleRegisterOnShowHeaderSpotlight,
	allowedFeatures,
	contentData,
	renderCopyLink,
	renderShare,
	isSpaceOverviewPage,
	readOnly,
	isConvertToFolderHighlighted = false,
	setIsConvertToFolderHighlighted,
	isVersionHistoryPage,
	isAttachmentsPage,
}: HeaderItemData): HeaderItems[] => {
	const isExternal = isExternalShareRequest();
	const { isAnonymous } = useContext(SPAViewContext);
	const { loomExternalOpenRoute } = useEmbeddedViewPageFeatures();

	const {
		isPublishingContent,
		onClickPublish,
		editorTitle,
		saveDraft,
		isUnpublishedDraft,
		shouldRenderPreview,
		onPreviewClicked,
		retrieveEditorTitleAndContent,
		viewMode,
		onHoverPublish,
		checkIfConditionPreventingPublish,
	} = useEditorObjectHeaderContext();

	const { isPublishDisabledForPublishModal, isPublishDisabled, isCloseButtonDisabled } =
		useDisableEditorActions(contentId, contentMode);

	const isClassicEdit =
		(contentType === 'page' || contentType === 'blogpost') &&
		(contentMode === 'draft' || contentMode === 'edit');

	const isCompanyHub = isCompanyHubSpaceKey(contentData?.space?.key);

	const isClassificationEnabled = useIsClassificationEnabledForContentTypeExpansion(
		contentData?.type ?? '',
	);
	const isArchived = contentData?.status === 'archived';

	const version = useObjectHeaderVersion({ contentData, useDraftVersion: isClassicEdit });
	const isPrivacyModeEnabled = useIsPrivacyModeEnabled();
	const { isMobileApp } = useIsMobileApp();

	const computedReadOnly =
		readOnly ||
		isExternal ||
		!contentData?.operations?.some(
			(operation) =>
				operation?.targetType &&
				VALID_OPERATION_TARGETS.includes(operation?.targetType as ContentType) &&
				operation?.operation === 'update',
		);

	const isClassificationAllowedInMoreActions = !computedReadOnly && isClassificationEnabled;
	const moreActionsAllowedFeatures = isClassificationAllowedInMoreActions
		? { ...allowedFeatures, showClassificationMenuAction: true }
		: allowedFeatures;

	const hasArchivePermission = contentData?.operations?.some((operation) => {
		return operation?.targetType === contentType && operation?.operation === 'archive';
	});

	const isPreviewPanelTestEnabled =
		StatsigConfigurations.getString('confluence_page_preview_panel_testing', '') !== '';

	const isCanvasPresenceEnabled =
		!isMobileApp &&
		contentMode !== 'archived' &&
		(isCanvasContentType(contentType) || contentType === 'database');

	const isPagePresenceEnabled =
		['page', 'blogpost'].includes(contentType) && contentMode !== 'archived';

	const isFolderPresenceEnabled =
		contentType === 'folder' &&
		contentMode !== 'archived' &&
		expVal('confluence_folder_view', 'isEnabled', false);
	const isAgentPresenceSupportedContent =
		['page', 'blogpost'].includes(contentType) && contentMode !== 'archived';
	const isAgentPresenceEnabled =
		isAgentPresenceSupportedContent &&
		(contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft');
	const isMovedAgentPresenceEnabled =
		isAgentPresenceSupportedContent && fg('platform_move_presence_agents');
	const shouldRenderPresence =
		allowedFeatures.showPresence !== false &&
		(isPagePresenceEnabled || isCanvasPresenceEnabled || isFolderPresenceEnabled) &&
		!isPrivacyModeEnabled;

	if (isVersionHistoryPage) {
		// don't show any header items or overflow items for version history page as they are irrelevant
		return [];
	}

	if (isAttachmentsPage && expValEquals('cc_attachments_page', 'isEnabled', true)) {
		// don't show any header items or overflow items for attachments page as they are irrelevant
		return [];
	}

	const currentUserHasEditPermission = contentData?.operations?.some((operation) => {
		return operation?.targetType === contentType && operation?.operation === 'update';
	});
	return [
		{
			name: 'lastEdited',
			shouldShowSeparator: false,
			shouldRender:
				allowedFeatures.showLastEdited !== false &&
				contentType !== 'embed' &&
				(contentType !== 'folder' || isFolderPresenceEnabled) &&
				(contentType === 'database' || !isSpaceOverviewPage),
			children: (
				<ErrorBoundary attribution={Attribution.DISCO}>
					<UnpublishedChangesOrLastEdited
						contentId={contentId}
						version={version}
						contentType={contentType as ShareContentType}
						contentMode={contentMode}
						spaceKey={contentData?.space?.key ?? ''}
						hasEditPermission={currentUserHasEditPermission ?? false}
					/>
				</ErrorBoundary>
			),
		},
		{
			name: 'aiAgentPresence',
			shouldShowSeparator: false,
			shouldRender: isAgentPresenceEnabled && !isMovedAgentPresenceEnabled,
			children: <AgentPresenceLoader />,
		},
		{
			name: 'presence',
			shouldShowSeparator: isCompanyHub,
			shouldRender: shouldRenderPresence || isMovedAgentPresenceEnabled,
			children: (
				<>
					{shouldRenderPresence ? (
						<Presence
							collaborators={contentActions?.collaborators}
							contentId={contentId}
							contentType={contentType}
							contentMode={contentMode}
							isViewMode={viewMode}
						>
							{isMovedAgentPresenceEnabled ? (
								<AgentPresenceLoader isNestedInPresenceAvatarGroup />
							) : undefined}
						</Presence>
					) : (
						isMovedAgentPresenceEnabled && <AgentPresenceLoader />
					)}
				</>
			),
		},
		{
			name: 'preview',
			shouldShowSeparator: false,
			shouldRender: isCompanyHub,
			children: (
				<ErrorBoundary attribution={Attribution.MIGRATIONS_TOOLING}>
					<IconButton icon={ScreenIcon} appearance="subtle" label="" onClick={onPreviewClicked} />
				</ErrorBoundary>
			),
		},
		{
			name: 'presentSlides',
			shouldShowSeparator: false,
			shouldRender: contentType === 'slide',
			children: <PresentSlidesButton />,
		},
		{
			name: 'mainActions', // this only handles showing external link for embed object header
			shouldShowSeparator: false,
			shouldRender: !!contentActions?.mainActions && !!allowedFeatures.showMainActions,
			children:
				!!contentActions?.mainActions && !!allowedFeatures.showMainActions ? (
					<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
						<MainActionsComponent mainActions={contentActions.mainActions.mainActions} />
					</ErrorBoundary>
				) : null,
		},
		{
			name: 'editIcon',
			shouldShowSeparator: false,
			shouldRender:
				(contentType === 'page' || contentType === 'blogpost') && contentMode === 'view',
			children: (
				<ErrorBoundary attribution={Attribution.PAGE_MODES}>
					<EditContentButton contentId={contentId} />
				</ErrorBoundary>
			),
		},
		{
			name: 'previewPanelTest',
			shouldShowSeparator: false,
			shouldRender: isPreviewPanelTestEnabled,
			children: <PreviewPanelTestButton />,
		},
		{
			name: 'update',
			shouldShowSeparator: false,
			shouldRender: isClassicEdit,
			children: (
				<ErrorBoundary attribution={Attribution.PAGE_MODES}>
					<TwoStageHydration
						name="update-split-button"
						// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
						enabled={!expValEquals('cc_editor_two_stage_hydration_removal', 'useHeaderItems', true)}
						ssrPlaceholder
					>
						<Subscribe to={[FlagsStateContainer]}>
							{(flags: FlagsStateContainer) => (
								<PublishButtonWithMoreOptions
									onClickPublish={onClickPublish}
									onHoverPublish={onHoverPublish}
									publishButtonDisabled={isPublishDisabledForPublishModal ?? isPublishDisabled}
									title={editorTitle}
									onClickPreview={onPreviewClicked}
									userViewingPreview={shouldRenderPreview}
									retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
									isUnpublishedDraft={isUnpublishedDraft}
									flags={flags}
									saveDraft={saveDraft}
									contentType={contentType}
									savingInProgress={isPublishingContent}
									shouldShowWorldIcon={false}
									shouldShowFooterButtons
									checkIfConditionPreventingPublish={checkIfConditionPreventingPublish}
								/>
							)}
						</Subscribe>
					</TwoStageHydration>
				</ErrorBoundary>
			),
		},
		{
			name: 'close',
			shouldShowSeparator: false,
			shouldRender: contentMode === 'draft' || contentMode === 'edit',
			children: (
				<ErrorBoundary attribution={Attribution.PAGE_MODES}>
					<EditorCloseButton
						contentType={contentType}
						contentId={contentId}
						spaceKey={contentData?.space?.key ?? ''}
						areMainButtonsDisabled={!!isCloseButtonDisabled}
						isUnpublishedDraft={contentMode === 'draft'}
					/>
				</ErrorBoundary>
			),
		},
		{
			name: 'share',
			shouldShowSeparator: false,
			shouldRender: !isAnonymous && !isArchived && !!allowedFeatures.showShare && !isCompanyHub,
			children: renderShare(),
		},
		{
			name: 'loomExternalOpen',
			shouldShowSeparator: false,
			shouldRender: !!loomExternalOpenRoute,
			children: loomExternalOpenRoute ? <LoomExternalOpenButton /> : null,
		},
		{
			name: 'contentRestrictions',
			shouldShowSeparator: false,
			shouldRender: !isAnonymous && !!allowedFeatures.showContentRestrictions && isArchived,
			children: (
				<ContentRestrictionsTrigger
					contentId={contentId}
					renderShareAndRestrictButton={renderShare}
				/>
			),
		},
		{
			name: 'copy',
			shouldShowSeparator: false,
			shouldRender: isArchived,
			children: (
				<ArchivedCopyLinkTrigger
					renderCopyLink={renderCopyLink}
					isRestrictionsButtonShown={!isAnonymous && !!allowedFeatures.showContentRestrictions}
				/>
			),
		},
		{
			name: 'moreActions',
			shouldShowSeparator: false,
			shouldRenderInContainer: false,
			shouldRender: !!allowedFeatures.showMoreActions,
			children: (
				<ErrorBoundary attribution={Attribution.DISCO}>
					<ContentOwnershipContextProvider>
						<Subscribe to={[FlagsStateContainer]}>
							{(flags: FlagsStateContainer) => (
								<MoreActionsComponent
									moreActions={contentActions?.moreActions}
									contentId={contentId}
									contentType={contentType}
									contentMode={contentMode}
									spaceKey={contentData?.space?.key ?? ''}
									allowedFeatures={moreActionsAllowedFeatures}
									hasArchivePermission={hasArchivePermission}
									handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
									flags={flags}
									contentData={contentData}
									readOnly={computedReadOnly}
									isConvertToFolderHighlighted={isConvertToFolderHighlighted}
									setIsConvertToFolderHighlighted={setIsConvertToFolderHighlighted}
								/>
							)}
						</Subscribe>
					</ContentOwnershipContextProvider>
				</ErrorBoundary>
			),
		},
	];
};
